import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useSession } from '../App.jsx';
import { ago, day, inr, num } from '../lib/format.js';
import { Alert, Card, CardSkeleton, Empty, StatCard, useAsync } from '../components/ui.jsx';
import { thisMonth } from '../lib/dates.js';
import PeriodPicker from '../components/PeriodPicker.jsx';
import ExportButton, { periodSlug, periodText } from '../components/ExportButton.jsx';

const KIND = {
  missing_in_tally: { label: 'Not in Tally', tone: 'attention' },
  missing_in_invit: { label: 'Only in Tally', tone: 'attention' },
  amount_mismatch: { label: 'Value differs', tone: 'attention' },
  cancelled_in_tally: { label: 'Cancelled in Tally', tone: 'attention' },
};

/** Two records of the same month, compared. The differences are the work. */
export default function Reconciliation() {
  const session = useSession();
  const entityId = session.entities[0] && session.entities[0].id;
  const [period, setPeriod] = useState(thisMonth);
  const { from, to } = period;
  const [kind, setKind] = useState('');
  const rec = useAsync(() => api.get(
    `/api/reconciliation?entity_id=${entityId}&from=${from}&to=${to}`), [entityId, from, to]);

  if (rec.error) return <Alert onRetry={rec.reload}>{rec.error}</Alert>;

  const findings = rec.data ? rec.data.findings.filter((f) => !kind || f.kind === kind) : [];

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Tally reconciliation</h1>
          <div className="page-sub">
            What InvIT issued against what Tally holds. A sync that fails quietly shows up here as a gap, and so does
            an invoice raised directly in Tally — which would carry no batch or specification record.
          </div>
        </div>
        <div className="toolbar" style={{ marginBottom: 0 }}>
          <PeriodPicker value={period} onChange={setPeriod} />
          <ExportButton disabled={!rec.data} build={() => ({
            filename: `reconciliation-${periodSlug(period)}`,
            title: 'Tally reconciliation',
            subtitle: periodText(period),
            sheets: [{
              name: 'Differences',
              columns: [
                { key: 'finding', header: 'Finding' }, { key: 'number', header: 'Document' },
                { key: 'voucher_type', header: 'Voucher type' }, { key: 'party', header: 'Party' },
                { key: 'date', header: 'Date', type: 'date' },
                { key: 'our_amount', header: 'InvIT', type: 'money', total: true },
                { key: 'their_amount', header: 'Tally', type: 'money', total: true },
                { key: 'note', header: 'What to do' },
              ],
              rows: rec.data.findings.map((f) => ({ ...f, finding: KIND[f.kind] ? KIND[f.kind].label : f.kind })),
            }],
          })} />
        </div>
      </div>

      {rec.loading && !rec.data && <CardSkeleton lines={5} />}

      {rec.data && (
        <>
          <div className="grid kpi" style={{ marginBottom: 16 }}>
            <StatCard label="InvIT" value={inr(rec.data.totals.ours_value, 0)}
              foot={`${rec.data.totals.ours_count} documents issued`} />
            <StatCard label="Tally" value={inr(rec.data.totals.theirs_value, 0)}
              foot={`${rec.data.totals.theirs_count} vouchers · register read ${ago(rec.data.register_synced_at)}`} />
            <StatCard label="Difference" value={inr(rec.data.totals.difference, 0)}
              foot={Math.abs(num(rec.data.totals.difference)) < 1 ? 'the two agree' : 'needs explaining'}
              tone={Math.abs(num(rec.data.totals.difference)) >= 1 ? 'attention' : undefined} />
            <StatCard label="Differences found" value={findings.length}
              foot={Object.entries(rec.data.summary).filter(([, v]) => v > 0)
                .map(([k, v]) => `${v} ${KIND[k].label.toLowerCase()}`).join(' · ') || 'none'}
              tone={findings.length ? 'attention' : undefined} />
          </div>

          <div className="segmented" style={{ marginBottom: 14 }} role="group" aria-label="Filter">
            {[['', 'All'], ...Object.entries(KIND).map(([k, v]) => [k, `${v.label} (${rec.data.summary[k]})`])]
              .map(([key, label]) => (
                <button key={label} aria-pressed={kind === key} onClick={() => setKind(key)}>{label}</button>
              ))}
          </div>

          <Card pad={findings.length === 0}>
            {findings.length === 0 ? (
              <Empty title="Nothing to explain">
                Every document InvIT issued in this period is in Tally at the same value, and Tally holds nothing
                InvIT does not know about.
              </Empty>
            ) : (
              <div className="table-wrap" style={{ margin: 0, padding: 0 }}>
                <table>
                  <thead><tr><th>Finding</th><th>Document</th><th>Party</th><th>Date</th>
                    <th className="r">InvIT</th><th className="r">Tally</th><th>What to do</th></tr></thead>
                  <tbody>
                    {findings.map((f, i) => (
                      <tr key={`${f.kind}-${f.number}-${i}`}>
                        <td><span className="pill bad">{KIND[f.kind].label}</span></td>
                        <td className="mono">
                          {f.id && f.doc === 'invoice'
                            ? <Link to={`/invoices/${f.id}`}>{f.number}</Link>
                            : f.id && f.doc === 'credit_note'
                              ? <Link to={`/credit-notes/${f.id}`}>{f.number}</Link>
                              : f.number}
                          <div className="sub">{f.voucher_type}</div>
                        </td>
                        <td>{f.party || '—'}</td>
                        <td>{day(f.date)}</td>
                        <td className="r num">{f.our_amount === null ? '—' : inr(f.our_amount, 0)}</td>
                        <td className="r num">{f.their_amount === null ? '—' : inr(f.their_amount, 0)}</td>
                        <td className="sub" style={{ maxWidth: 320 }}>{f.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </>
  );
}
